import React from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { QueryClient, QueryClientProvider } from 'react-query'
import {
	createTheme,
	ThemeProvider,
	extendTheme as materialExtendTheme,
	CssVarsProvider as MaterialCssVarsProvider,
	THEME_ID as MATERIAL_THEME_ID,
} from '@mui/material/styles'
import { koKR } from '@mui/x-data-grid/locales'
import { koKR as pickersKoKR } from '@mui/x-date-pickers/locales'
import { koKR as coreKoKR } from '@mui/material/locale'

import { View } from '@components/atoms'
import { AppWrapper, CookieConsentBanner } from '@components/systems'

import { useAppSelector, ConfigStateType } from '@redux/index'
import RouteProvider from '@routes/RouteProvider'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { ko } from 'date-fns/locale'
import { blue, deepPurple, pink } from '@mui/material/colors'
import RouteChangeTracker from './RouteChangeTracker'

import { CssVarsProvider as JoyCssVarsProvider } from '@mui/joy/styles'
import CssBaseline from '@mui/material/CssBaseline'
import 'react-international-phone/style.css'
import './App.css'

const App = () => {
	// redux state
	const mode: ConfigStateType['mode'] = useAppSelector(state => state.config.mode)
	const backgroundColor: string = useAppSelector(state => state.config.backgroundColor)
	const mainBackgroundColor: string = useAppSelector(
		state => state.config.mainBackgroundColor,
	)
	const isLogin: boolean = useAppSelector(state => state.auth.isLogin as boolean)
	const userLanguage: string = useAppSelector(state => state.user.language)

	RouteChangeTracker()

	React.useEffect(() => {
		const newBackgroundColor: string = isLogin ? mainBackgroundColor : backgroundColor

		document.body.style.backgroundColor = newBackgroundColor
	}, [isLogin, backgroundColor, mainBackgroundColor])

	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				retry: 0,
				suspense: true,
			},
		},
	})

	const primaryColor = mode === 'admin' ? blue : deepPurple

	const theme = React.useMemo(() => {
		const initTheme = {
			palette: {
				primary: {
					...primaryColor,
					main: mode === 'admin' ? blue['A400'] : deepPurple['A400'],
				},
				error: {
					...pink,
					main: pink['A400'],
				},
			},
		}

		const res =
			userLanguage === 'ko'
				? createTheme(initTheme, koKR, pickersKoKR, coreKoKR)
				: createTheme(initTheme)

		return res
	}, [userLanguage, mode])

	const materialTheme = materialExtendTheme({
		colorSchemes: {
			light: {
				palette: {
					primary: {
						main: mode === 'admin' ? blue['A400'] : deepPurple['A400'],
					},
				},
			},
		},
	})

	return (
		<HelmetProvider>
			<MaterialCssVarsProvider theme={{ [MATERIAL_THEME_ID]: materialTheme }}>
				<CssBaseline enableColorScheme />
				<QueryClientProvider client={queryClient}>
					<ThemeProvider theme={theme}>
						<JoyCssVarsProvider>
							{/*
								날짜 입력은 전부 `yyyy-MM-dd` 로 쓴다. 여기서 한 번 걸어 두면
								화면마다 LocalizationProvider 를 감싸지 않아도 된다.
								⚠ 네이티브 `<input type="date">` 는 이 설정을 따르지 않는다 —
								브라우저 로케일을 쓰므로 mm/dd/yyyy 로 나온다. DatePicker 를 쓸 것.
							*/}
							<LocalizationProvider
								dateAdapter={AdapterDateFns}
								adapterLocale={ko}
								dateFormats={{ keyboardDate: 'yyyy-MM-dd' }}
							>
								<AppWrapper>
									<View className="App">
										<RouteProvider />
									</View>
									<CookieConsentBanner />
								</AppWrapper>
							</LocalizationProvider>
						</JoyCssVarsProvider>
					</ThemeProvider>
				</QueryClientProvider>
			</MaterialCssVarsProvider>
		</HelmetProvider>
	)
}

export default App
